import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGuestGate } from "../lib/useGuestGate";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Plus, Trash2, ArrowUpRight, ArrowDownRight, Pencil } from "lucide-react";
import { toast } from "sonner";

const money = (n) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Number(n)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function computePL(item) {
  return Number(item.current_price) - Number(item.purchase_price) + Number(item.dividends || 0);
}

const EMPTY_FORM = { ticker: "", purchasePrice: "", currentPrice: "", dividends: "" };

export default function PortfolioSection() {
  const { gate } = useGuestGate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state: mode = "create" | "edit" | null
  const [mode, setMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portfolio");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load portfolio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    if (gate("Adding a holding")) return;
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMode("create");
  };

  const openEdit = (it) => {
    if (gate("Editing a holding")) return;
    setEditingId(it.id);
    setForm({
      ticker: it.ticker,
      purchasePrice: String(it.purchase_price),
      currentPrice: String(it.current_price),
      dividends: String(it.dividends ?? 0),
    });
    setMode("edit");
  };

  const closeDialog = () => {
    setMode(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ticker.trim()) return;
    setSubmitting(true);
    const body = {
      ticker: form.ticker.trim().toUpperCase(),
      purchase_price: Number(form.purchasePrice),
      current_price: Number(form.currentPrice),
      dividends: Number(form.dividends || 0),
    };
    try {
      if (mode === "edit" && editingId) {
        const { data } = await api.put(`/portfolio/${editingId}`, body);
        setItems((prev) => prev.map((it) => (it.id === editingId ? data : it)));
        toast.success(`${data.ticker} updated`);
      } else {
        const { data } = await api.post("/portfolio", body);
        setItems((prev) => [data, ...prev]);
        toast.success(`${data.ticker} added to portfolio`);
      }
      closeDialog();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          (mode === "edit" ? "Failed to update holding" : "Failed to add holding")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, tk) => {
    if (gate("Removing a holding")) return;
    try {
      await api.delete(`/portfolio/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success(`${tk} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove holding");
    }
  };

  const totalPL = items.reduce((acc, it) => acc + computePL(it), 0);
  const totalCost = items.reduce((acc, it) => acc + Number(it.purchase_price), 0);
  const totalPct = totalCost > 0 ? (totalPL / totalCost) * 100 : null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="space-y-6" data-testid="portfolio-section">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-1">
            Our Holdings
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 font-heading">
            Portfolio
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Track your open positions. Profit/loss per share is calculated as{" "}
            <span className="font-mono text-slate-700">current − purchase + dividends</span>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <div
              className={`text-right ${totalPL >= 0 ? "text-emerald-700" : "text-red-600"}`}
              data-testid="portfolio-total"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                Total P/L
              </div>
              <div className="font-mono tabular-nums text-lg font-semibold">
                {money(totalPL)}
                {totalPct !== null && (
                  <span className="text-xs ml-1 text-slate-500">
                    ({totalPct >= 0 ? "+" : ""}
                    {totalPct.toFixed(2)}%)
                  </span>
                )}
              </div>
            </div>
          )}
          <Button
            onClick={openCreate}
            className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
            data-testid="add-portfolio-button"
          >
            <Plus className="w-4 h-4" />
            Add Holding
          </Button>
        </div>
      </div>

      <Dialog open={mode !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="portfolio-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {mode === "edit" ? "Edit Holding" : "Add Portfolio Holding"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label
                htmlFor="pf-ticker"
                className="text-xs tracking-[0.15em] uppercase text-slate-600"
              >
                Ticker
              </Label>
              <Input
                id="pf-ticker"
                required
                value={form.ticker}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ticker: e.target.value.toUpperCase() }))
                }
                placeholder="AAPL"
                maxLength={10}
                className="mt-1.5 font-mono uppercase"
                data-testid="pf-ticker-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-baseline justify-between">
                  <Label
                    htmlFor="pf-purchase"
                    className="text-xs tracking-[0.15em] uppercase text-slate-600"
                  >
                    Purchase Price
                  </Label>
                  <span className="text-xs text-slate-400">$</span>
                </div>
                <Input
                  id="pf-purchase"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.purchasePrice}
                  onChange={set("purchasePrice")}
                  placeholder="150.00"
                  className="mt-1.5 font-mono tabular-nums"
                  data-testid="pf-purchase-input"
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <Label
                    htmlFor="pf-current"
                    className="text-xs tracking-[0.15em] uppercase text-slate-600"
                  >
                    Current Price
                  </Label>
                  <span className="text-xs text-slate-400">$</span>
                </div>
                <Input
                  id="pf-current"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.currentPrice}
                  onChange={set("currentPrice")}
                  placeholder="180.25"
                  className="mt-1.5 font-mono tabular-nums"
                  data-testid="pf-current-input"
                />
              </div>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label
                  htmlFor="pf-dividends"
                  className="text-xs tracking-[0.15em] uppercase text-slate-600"
                >
                  Dividends Received
                </Label>
                <span className="text-xs text-slate-400">$</span>
              </div>
              <Input
                id="pf-dividends"
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                value={form.dividends}
                onChange={set("dividends")}
                placeholder="0.00"
                className="mt-1.5 font-mono tabular-nums"
                data-testid="pf-dividends-input"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                data-testid="cancel-portfolio-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="submit-portfolio-button"
              >
                {submitting
                  ? "Saving..."
                  : mode === "edit"
                  ? "Save Changes"
                  : "Add Holding"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="portfolio-table"
      >
        <table className="w-full min-w-[720px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Ticker
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Purchase
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Current
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Dividends
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                P/L
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[120px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-slate-500">
                  Loading portfolio...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center" data-testid="empty-portfolio">
                  <div className="text-sm text-slate-500">No holdings yet.</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Click "Add Holding" to track your first position.
                  </div>
                </td>
              </tr>
            ) : (
              items.map((it) => {
                const pl = computePL(it);
                const pct =
                  Number(it.purchase_price) > 0
                    ? (pl / Number(it.purchase_price)) * 100
                    : null;
                const positive = pl >= 0;
                return (
                  <tr
                    key={it.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`portfolio-row-${it.ticker}`}
                  >
                    <td className="py-4 px-6">
                      <span className="font-mono font-semibold text-slate-900 text-base tracking-wide">
                        {it.ticker}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right font-mono tabular-nums text-sm text-slate-700">
                      {money(it.purchase_price)}
                    </td>
                    <td className="py-4 px-6 text-right font-mono tabular-nums text-sm text-slate-700">
                      {money(it.current_price)}
                    </td>
                    <td className="py-4 px-6 text-right font-mono tabular-nums text-sm text-slate-700">
                      {money(it.dividends || 0)}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div
                        className={`inline-flex items-center gap-1.5 font-mono tabular-nums text-sm font-semibold ${
                          positive ? "text-emerald-700" : "text-red-600"
                        }`}
                        data-testid={`pl-${it.ticker}`}
                      >
                        {positive ? (
                          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.25} />
                        ) : (
                          <ArrowDownRight className="w-3.5 h-3.5" strokeWidth={2.25} />
                        )}
                        <span>{money(pl)}</span>
                        {pct !== null && (
                          <span className="text-xs font-normal text-slate-500">
                            ({pct >= 0 ? "+" : ""}
                            {pct.toFixed(2)}%)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right w-[120px]">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => openEdit(it)}
                          aria-label={`Edit ${it.ticker}`}
                          title={`Edit ${it.ticker}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                          data-testid={`edit-portfolio-${it.id}`}
                        >
                          <Pencil className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(it.id, it.ticker)}
                          aria-label={`Remove ${it.ticker}`}
                          title={`Remove ${it.ticker}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                          data-testid={`delete-portfolio-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
